import { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * RecapCard — thin retention strip on /today:
 *   Left  → Yesterday: X/Y done + score.
 *   Right → 7-day momentum sparkline + today's delta.
 *
 * Renders NOTHING when the user has no history (day-1 keeps the hero clean).
 */
export default function RecapCard({ recap }) {
  // Sparkline geometry constants (need to be in outer scope so hooks below can use them).
  const width = 120;
  const height = 34;
  const pad = 3;

  const series = recap?.series || [];

  const path = useMemo(() => {
    if (!series.length) return "";
    const max = Math.max(1, ...series.map((s) => s.overall));
    const step = (width - pad * 2) / Math.max(1, series.length - 1);
    return series
      .map((s, i) => {
        const x = pad + i * step;
        const y = height - pad - ((s.overall / max) * (height - pad * 2));
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }, [series]);

  const lastPoint = useMemo(() => {
    if (!series.length) return null;
    const max = Math.max(1, ...series.map((s) => s.overall));
    const step = (width - pad * 2) / Math.max(1, series.length - 1);
    const i = series.length - 1;
    return {
      x: pad + i * step,
      y: height - pad - ((series[i].overall / max) * (height - pad * 2)),
    };
  }, [series]);

  if (!recap || !recap.has_history) return null;

  const { yesterday, delta } = recap;

  const deltaColor = delta > 0 ? "#7ac794" : delta < 0 ? "#c97a6a" : "var(--ink-55)";
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;

  const yestLine = yesterday.had_data
    ? `Yesterday: ${yesterday.done}/${yesterday.total} moves · Momentum was ${yesterday.score}`
    : "Yesterday was a rest day — no moves logged.";

  return (
    <div
      data-testid="today-recap"
      className="rounded-xl px-4 py-3 mb-6 flex items-center justify-between gap-4"
      style={{
        background: "rgba(216,168,87,0.05)",
        border: "1px solid rgba(216,168,87,0.20)",
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-0.5" style={{ color: "var(--seal)" }}>
          Yesterday · 7-day trend
        </div>
        <div
          data-testid="recap-yesterday-line"
          className="text-sm truncate"
          style={{ color: "var(--ink-72)" }}
        >
          {yestLine}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <svg width={width} height={height} data-testid="recap-sparkline" aria-hidden>
          <defs>
            <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--seal)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--seal)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {path && (
            <>
              <path
                d={`${path} L ${width - pad} ${height - pad} L ${pad} ${height - pad} Z`}
                fill="url(#spark-grad)"
              />
              <path d={path} stroke="var(--seal)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              {lastPoint && (
                <circle cx={lastPoint.x} cy={lastPoint.y} r="2.4" fill="var(--seal)" />
              )}
            </>
          )}
        </svg>
        <div
          data-testid="recap-delta"
          className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-full"
          style={{
            background: "var(--surface)",
            border: `1px solid ${deltaColor}55`,
            color: deltaColor,
          }}
          title="Today vs Yesterday"
        >
          <DeltaIcon size={11} strokeWidth={2} />
          {delta > 0 ? `+${delta}` : delta}
        </div>
      </div>
    </div>
  );
}
